import { SubjectType } from "@custom-types/base-types";
import { FaroCoreApiConst } from "@stellar/core-api-client";
import { DateTimeUtils } from "@stellar/utils";
import {
  BaseProduct,
  ProductConfig,
  ProductLimitsConfig,
  ProductMap,
  ProductType,
  ScopeConfig,
} from "@utils/product/product-utils-types";
import { DeepPartial } from "@utils/utility-types";

function createBaseProduct<T extends ProductType>(
  identifier: ProductMap<T>,
  overrides?: DeepPartial<Omit<BaseProduct, "identifier">>
): BaseProduct {
  const defaultScope: ScopeConfig = {
    shouldShow: false,
    limits: {
      type: "none",
    },
  };

  const base: BaseProduct = {
    identifier,
    identifiers: [identifier],
    scope: Object.fromEntries(
      FaroCoreApiConst.allSubjectTypes.map((subjectType) => {
        const scopeOverride = overrides?.scope?.[subjectType];
        return [
          subjectType,
          {
            ...defaultScope,
            ...scopeOverride,
            limits: (scopeOverride?.limits ??
              defaultScope.limits) as ProductLimitsConfig,
          },
        ];
      })
    ) as Record<SubjectType, ScopeConfig>,
    duration: {
      defaultDays: DateTimeUtils.DURATION.days.inYear,
      isFixed: false,
    },
    isCoreProduct: false,
    hasSceneSeats: false,
    apps: [],
    includedTokens: null,
  };

  return {
    ...base,
    ...overrides,
    scope: base.scope,
    duration: {
      ...base.duration,
      ...overrides?.duration,
    },
  };
}

/** Whether the limits should be shown for the selected product */
export function shouldShowProductLimits(
  product: BaseProduct,
  planSubjectType: SubjectType
): boolean {
  const limitType = product.scope[planSubjectType].limits.type;
  return (
    limitType === "editable" ||
    limitType === "editable-with-defaults" ||
    limitType === "required-with-defaults"
  );
}

function buildScopeFromConfig(
  config: ProductConfig
): Record<SubjectType, ScopeConfig> {
  return Object.fromEntries(
    FaroCoreApiConst.allSubjectTypes.map((subjectType) => {
      const defaultsHidden =
        config.scopesWithDefaultsHiddenLimit?.[subjectType];
      const requiredDefaults =
        config.scopesWithRequiredDefaultsLimit?.[subjectType];
      const isEditable =
        config.scopesWithEditableLimit?.includes(subjectType) ?? false;
      const isWithoutLimit =
        config.scopesWithoutLimit?.includes(subjectType) ?? false;

      let limits: ProductLimitsConfig = { type: "none" };
      if (defaultsHidden) {
        limits = { type: "defaults-hidden", defaults: defaultsHidden };
      } else if (requiredDefaults) {
        limits = { type: "required-with-defaults", defaults: requiredDefaults };
      } else if (isEditable) {
        limits = { type: "editable" };
      }

      return [
        subjectType,
        {
          shouldShow:
            Boolean(defaultsHidden) ||
            Boolean(requiredDefaults) ||
            isEditable ||
            isWithoutLimit,
          limits,
        },
      ];
    })
  ) as Record<SubjectType, ScopeConfig>;
}

/**
 * Build a `Record<Identifier, BaseProduct>` from a product config map,
 * applying `scopesWithoutLimit` / `scopesWithEditableLimit` /
 * `scopesWithDefaultsHiddenLimit` to the scope.
 */
export function createProductsFromConfigs<
  T extends ProductType,
  TIdentifier extends ProductMap<T>,
>(
  configs: Record<TIdentifier, ProductConfig>
): Record<TIdentifier, BaseProduct> {
  return Object.entries(configs).reduce(
    (acc, [identifier, config]) => {
      const productConfig = config as ProductConfig;
      acc[identifier as TIdentifier] = createBaseProduct(
        identifier as ProductMap<T>,
        {
          identifiers:
            productConfig.identifiers ?? ([identifier] as ProductMap<T>[]),
          name: productConfig.name,
          description: productConfig.description,
          isCoreProduct: productConfig.isCoreProduct,
          hasSceneSeats: productConfig.hasSceneSeats ?? false,
          apps: productConfig.apps ?? [],
          includedTokens: productConfig.includedTokens ?? null,
          duration: productConfig.duration,
          scope: buildScopeFromConfig(productConfig),
        }
      );
      return acc;
    },
    {} as Record<TIdentifier, BaseProduct>
  );
}
